import React, { useRef, useState } from "react";
import {
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  Tooltip,
  Zoom,
} from "@mui/material";
import {
  AddOutlined,
  Info,
  InfoOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { MAX_ARCHIVOS } from "../../../domain/evento/schema";
import { subirArchivos, descartarArchivo, MAX_TAMANIO_TEXTO } from "../../../utils/media";
import SeccionEvento from "./SeccionEvento";

/* El rol de cada archivo lo define su posición, que es la misma regla que
 * aplica el backend al guardar. */
const rolDeArchivo = (indice, total) => {
  if (total === 1) return "Imagen de perfil y de portada";
  if (indice === 0) return "Imagen de perfil";
  if (indice === 1) return "Imagen de portada";
  return "Imagen de descripción";
};

const BadgeRol = ({ indice, total, tamanio = 18, placement = "right" }) => (
  <Tooltip arrow TransitionComponent={Zoom} placement={placement} title={rolDeArchivo(indice, total)}>
    <Info
      sx={{
        width: `${tamanio}px`,
        height: `${tamanio}px`,
        position: "absolute",
        color: "#FFD400",
        top: "5px",
        right: "5px",
        zIndex: 200,
      }}
    />
  </Tooltip>
);

/* Sección de contenido visual.
 *
 * `media` es un único array de {url, type, format, public_id}: los archivos se
 * suben apenas se eligen, así que no hace falta arrastrar objetos File ni
 * generar object URLs, y reordenar mueve el item entero en lugar de mover
 * algunos arrays paralelos y dejar los tipos desalineados. */
const ImageSection = ({ media, onChange, errores, abierta, onToggle }) => {
  const [subiendo, setSubiendo] = useState(false);
  const [seleccionado, setSeleccionado] = useState(0);
  const inputRef = useRef(null);

  const total = media.length;
  const indiceActual = Math.min(seleccionado, Math.max(total - 1, 0));

  const elegirArchivos = async (e) => {
    const elegidos = Array.from(e.target.files ?? []);
    e.target.value = ""; // permite volver a elegir el mismo archivo
    if (!elegidos.length) return;

    if (total + elegidos.length > MAX_ARCHIVOS) {
      toast.error(`Podés subir como máximo ${MAX_ARCHIVOS} archivos.`);
      return;
    }

    setSubiendo(true);
    try {
      const { items, rechazados } = await subirArchivos(elegidos);
      rechazados.forEach((nombre) =>
        toast.error(`"${nombre}" supera el límite de ${MAX_TAMANIO_TEXTO}.`)
      );
      if (!items.length) return;

      onChange("media", [...media, ...items]);
      if (total === 0) setSeleccionado(0);
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error(error.message || "No se pudieron subir los archivos.");
    } finally {
      setSubiendo(false);
    }
  };

  const eliminarArchivo = async (indice) => {
    const item = media[indice];
    if (!item) return;

    // si es un archivo recién subido se borra; si ya era del evento, lo
    // borra el backend al guardar
    await descartarArchivo(item.public_id);

    onChange(
      "media",
      media.filter((_, i) => i !== indice)
    );
    setSeleccionado((prev) => (prev > 0 && prev >= indice ? prev - 1 : prev));
  };

  const reordenar = (origen, destino) => {
    if (origen === destino) return;
    const siguiente = [...media];
    const [movido] = siguiente.splice(origen, 1);
    siguiente.splice(destino, 0, movido);
    onChange("media", siguiente);
    setSeleccionado(destino);
  };

  const actual = media[indiceActual];

  return (
    <SeccionEvento
      id="img"
      titulo="Contenido visual"
      tituloExpandido="Agregá imágenes y videos *"
      descripcion="Subí imágenes y videos para destacar los momentos más importantes de tu evento."
      requerido
      tieneError={Boolean(errores.files)}
      abierta={abierta}
      onToggle={onToggle}
    >
      <FormControl fullWidth>
        <p className="descrip-seccion-evento-expanded" style={{ fontSize: "16px", lineHeight: "24px" }}>
          Subí <b>hasta {MAX_ARCHIVOS} imágenes y/o videos</b> que muestren los momentos
          clave de tu evento. <br />
          <span>
            Asegurate de elegir archivos de <b>alta calidad</b> para un gran impacto visual.
          </span>
        </p>
        <p className="descrip-seccion-evento-expanded" style={{ fontSize: "16px", marginBottom: 0 }}>
          IMPORTANTE:
        </p>
        <ul>
          <li>
            <b>Foto de perfil:</b> la primera imagen que subas será la principal del
            evento. Podés usar el logo de tu organización o del evento.
          </li>
          <li>
            <b>Portada:</b> la segunda imagen que subas será la portada del evento.
          </li>
          <li>
            <b>Galería:</b> podés subir hasta {MAX_ARCHIVOS - 2} imágenes más que se
            mostrarán en la descripción.
          </li>
        </ul>

        <div className="info-input-img">
          <div className="d-flex gap-2 justify-content-center align-items-center w-100 flex-wrap">
            <Chip
              icon={<InfoOutlined sx={{ color: "#5656FF !important" }} />}
              size="small"
              label={`Tamaño máximo de archivo: ${MAX_TAMANIO_TEXTO}`}
              sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
            />
            <Chip
              icon={<InfoOutlined sx={{ color: "#5656FF !important" }} />}
              size="small"
              label="Formatos admitidos de imagen: JPEG, PNG"
              sx={{ backgroundColor: "#E6E6FF", color: "#5656FF" }}
            />
          </div>
        </div>

        <input
          ref={inputRef}
          id="files"
          name="files"
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={elegirArchivos}
          style={{ display: "none" }}
        />

        <div className={`input-file ${total === 0 ? "" : "border-0"}`}>
          {subiendo ? (
            <div className="d-flex flex-column align-items-center">
              <p>Cargando archivos...</p>
              <CircularProgress />
            </div>
          ) : total === 0 ? (
            <label className="placeholder-input-img" htmlFor="files">
              <UploadFileOutlined
                sx={{
                  width: "46px",
                  height: "46px",
                  color: "#5656ff",
                  "&:hover": { color: "#221A60" },
                }}
              />
              Hacé clic acá para subir una imagen o video
            </label>
          ) : (
            <div className="imagen-contenedor">
              <div className="main-preview">
                {actual?.type === "video" ? (
                  <video src={actual.url} controls />
                ) : (
                  <img src={actual?.url} alt="Contenido seleccionado" />
                )}
                <div className="tooltip-fotos">
                  <BadgeRol indice={indiceActual} total={total} tamanio={25} placement="left" />
                </div>
                <button
                  type="button"
                  className="remove-button"
                  onClick={() => eliminarArchivo(indiceActual)}
                >
                  Eliminar
                </button>
              </div>

              <div className="thumbnails-contenedor">
                <div className="boton-agregar-img" onClick={() => inputRef.current?.click()}>
                  <AddOutlined sx={{ color: "#3659E3" }} />
                </div>
                {media.map((item, indice) => (
                  <div
                    key={item.public_id || item.url}
                    className={`thumbnail ${indiceActual === indice ? "selected" : ""}`}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", indice)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      reordenar(parseInt(e.dataTransfer.getData("text/plain"), 10), indice);
                    }}
                    onClick={() => setSeleccionado(indice)}
                  >
                    <BadgeRol indice={indice} total={total} />
                    {item.type === "video" ? (
                      <video src={item.url} />
                    ) : (
                      <img src={item.url} alt={`Vista previa ${indice + 1}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {errores.files && (
          <Alert severity="error" sx={{ marginTop: "10px" }}>
            {errores.files}
          </Alert>
        )}
      </FormControl>
    </SeccionEvento>
  );
};

export default ImageSection;
